import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { Capacitor } from '@capacitor/core';
import AppSearchAPIConnector from '@elastic/search-ui-app-search-connector';
import {
  EuiAvatar,
  EuiBadge,
  EuiButton,
  EuiButtonEmpty,
  EuiButtonIcon,
  EuiCallOut,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutFooter,
  EuiFlexItem,
  EuiFormRow,
  EuiGlobalToastList,
  EuiIcon,
  EuiLoadingSpinner,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiOverlayMask,
  EuiPanel,
  EuiPopover,
  EuiProgress,
  EuiSelect,
  EuiSpacer,
  EuiSwitch,
  EuiText,
  EuiTextArea,
  EuiTitle,
} from '@elastic/eui';
import TopRightMenuAvatar from './TopRightMenu';
import LanguageToggle from './LanguageToggle';
import { LoginRequiredModal } from './LoginRequiredModal';
import Verse from './Verse';
import ReadingPlanDayScripture from './ReadingPlanDayScripture';
import ReadingPlanCheckInCalendar from './ReadingPlanCheckInCalendar';
import ResponsiveAudioPlayer from './ResponsiveAudioPlayer';
import YTModal from './YTModal';
import { TranslateLanguage } from './KeywordTranslator';
import { parseReadingPlanReference } from '../utils/scriptureReferences';
import { buildSearchOptionsFromConfig, getConfig } from '../config/config-helper';
import {
  getShareResourcesWithParticipantsDefault,
  setShareResourcesWithParticipantsDefault,
} from './readingPlanPreferences';
import '../reading-plans.css';

const { hostIdentifier, searchKey, endpointBase, engineName } = getConfig();
const resourceSearchConnector = new AppSearchAPIConnector({
  searchKey,
  engineName,
  hostIdentifier,
  endpointBase,
});
const resourceSearchQueryConfig = buildSearchOptionsFromConfig();
const dateValue = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const todayValue = () => dateValue(new Date());
const dateOnlyValue = value => String(value || '').slice(0, 10);
const displayDate = value => {
  const match = dateOnlyValue(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).toLocaleDateString()
    : '';
};
const dayTitleWithoutNumber = day => {
  const title = String(day?.title || '').trim();
  if (!title) return '';
  const dayNumber = Number(day?.dayNumber);
  if (!Number.isInteger(dayNumber)) return title;
  return title.replace(new RegExp(`^第\\s*${dayNumber}\\s*天\\s*[:：]?\\s*`), '').trim();
};
const withClientDate = (config = {}) => ({
  ...config,
  headers: {
    ...(config.headers || {}),
    'X-Client-Local-Date': todayValue(),
    'X-Client-Time-Zone': Intl.DateTimeFormat().resolvedOptions().timeZone || '',
  },
});
const dateOrdinal = value => {
  const match = dateOnlyValue(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
};
const planDayDate = (startDate, dayNumber) => {
  const start = dateOrdinal(startDate);
  const offset = Number(dayNumber) - 1;
  return start !== null && Number.isInteger(offset) && offset >= 0
    ? new Date(start + offset * 86400000).toISOString().slice(0, 10)
    : '';
};
const getPlanTiming = plan => {
  const startDate = dateOnlyValue(plan?.myStartDate || plan?.fixedStartDate);
  const start = dateOrdinal(startDate);
  const duration = Math.max(Number(plan?.durationDays) || 0, 0);
  if (start === null || !duration) return { startDate, endDate: '', elapsedDays: null, isUpcoming: false, isOverdue: false };
  const today = new Date();
  const todayOrdinal = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const elapsedDays = Math.floor((todayOrdinal - start) / 86400000) + 1;
  const endDate = new Date(start + ((duration - 1) * 86400000)).toISOString().slice(0, 10);
  return {
    startDate,
    endDate,
    elapsedDays,
    isUpcoming: elapsedDays < 1,
    isOverdue: elapsedDays > duration && !isPlanCompleted(plan),
  };
};
const resultValue = (result, field) => result?.[field]?.raw ?? result?.[field]?.snippet ?? '';
const safeCoverImageUrl = value => {
  const url = String(value || '').trim();
  return /^(https?:\/\/|\/)/i.test(url) ? url : '';
};
const normalizePlan = plan => plan ? {
  ...plan,
  coverImageUrl: plan.coverImageUrl ?? plan.CoverImageUrl ?? '',
  startMode: plan.startMode ?? plan.StartMode ?? 'individual',
  fixedStartDate: plan.fixedStartDate ?? plan.FixedStartDate ?? null,
  myStartDate: plan.myStartDate ?? plan.MyStartDate ?? null,
} : plan;
const isPlanCompleted = plan => Boolean(
  plan?.isCompleted
  || (
    Number(plan?.durationDays) > 0
    && Number(plan?.completedDays) >= Number(plan?.durationDays)
  )
);
const isOngoingPlan = plan => Boolean(
  plan
  && !plan.isArchived
  && !isPlanCompleted(plan)
  && !getPlanTiming(plan).isOverdue
);
const lastOpenedPlanStorageKey = userId => `ctb.readingPlans.lastOngoingPlan.${userId}`;
const getLastOpenedPlanId = userId => {
  if (!userId) return null;
  try {
    const value = Number(localStorage.getItem(lastOpenedPlanStorageKey(userId)));
    return Number.isInteger(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
};
const setLastOpenedPlanId = (userId, planId) => {
  if (!userId || !planId) return;
  try {
    localStorage.setItem(lastOpenedPlanStorageKey(userId), String(planId));
  } catch {}
};
const clearLastOpenedPlanId = userId => {
  if (!userId) return;
  try {
    localStorage.removeItem(lastOpenedPlanStorageKey(userId));
  } catch {}
};
const defaultPlanFrom = plans => (
  plans.find(plan => !plan.isArchived && !isPlanCompleted(plan) && !getPlanTiming(plan).isOverdue)
  || plans.find(plan => !plan.isArchived && getPlanTiming(plan).isOverdue)
  || plans.find(plan => !plan.isArchived)
  || plans[0]
);
const coverBackgroundStyle = value => {
  const url = safeCoverImageUrl(value);
  if (!url) return undefined;
  const displayUrl = url.startsWith('/api/readingplans/cover-images/')
    ? `${url}${url.includes('?') ? '&' : '?'}asset=2`
    : url;
  return { backgroundImage: `linear-gradient(90deg, rgba(7, 45, 72, .56), rgba(7, 74, 117, .2)), url("${displayUrl.replace(/"/g, '%22')}")` };
};
const builtInPlanCovers = [
  { url: '/reading-plan-covers/dawn.svg', label: '晨光群山' },
  { url: '/reading-plan-covers/path.svg', label: '生命道路' },
  { url: '/reading-plan-covers/sea.svg', label: '平靜海面' },
  { url: '/reading-plan-covers/vine.svg', label: '葡萄樹' },
];

const coverCropWidth = 1600;
const coverCropHeight = 500;
const clampCropValue = value => Math.min(100, Math.max(0, value));
const supportedCoverExtension = fileName => /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(String(fileName || ''));
const isSupportedCoverFile = file => (
  /^image\/(jpeg|png|webp|gif|heic|heif)$/i.test(String(file?.type || ''))
  || (!file?.type && supportedCoverExtension(file?.name))
);

const drawCoverCrop = (canvas, crop) => {
  if (!canvas || !crop?.image) return;
  const context = canvas.getContext('2d');
  if (!context) return;
  const width = canvas.width;
  const height = canvas.height;
  const baseScale = Math.max(width / crop.image.naturalWidth, height / crop.image.naturalHeight);
  const scale = baseScale * crop.zoom;
  const renderedWidth = crop.image.naturalWidth * scale;
  const renderedHeight = crop.image.naturalHeight * scale;
  const offsetX = -(renderedWidth - width) * (crop.x / 100);
  const offsetY = -(renderedHeight - height) * (crop.y / 100);
  context.clearRect(0, 0, width, height);
  context.drawImage(crop.image, offsetX, offsetY, renderedWidth, renderedHeight);
};

const createCroppedCoverBlob = crop => new Promise((resolve, reject) => {
  const canvas = document.createElement('canvas');
  canvas.width = coverCropWidth;
  canvas.height = coverCropHeight;
  drawCoverCrop(canvas, crop);
  canvas.toBlob(blob => {
    if (blob) resolve(blob);
    else reject(new Error('無法生成裁剪圖片。'));
  }, 'image/jpeg', 0.9);
});

function ReadingPlanCoverCropper({ crop, onChange, language }) {
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const translate = text => TranslateLanguage(language, text);

  useEffect(() => {
    drawCoverCrop(canvasRef.current, crop);
  }, [crop]);

  const beginDrag = event => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const bounds = canvas.getBoundingClientRect();
    const baseScale = Math.max(bounds.width / crop.image.naturalWidth, bounds.height / crop.image.naturalHeight);
    const renderedWidth = crop.image.naturalWidth * baseScale * crop.zoom;
    const renderedHeight = crop.image.naturalHeight * baseScale * crop.zoom;
    dragRef.current = {
      clientX: event.clientX,
      clientY: event.clientY,
      x: crop.x,
      y: crop.y,
      overflowX: Math.max(renderedWidth - bounds.width, 0),
      overflowY: Math.max(renderedHeight - bounds.height, 0),
    };
  };

  const dragImage = event => {
    const drag = dragRef.current;
    if (!drag) return;
    const deltaX = event.clientX - drag.clientX;
    const deltaY = event.clientY - drag.clientY;
    onChange({
      ...crop,
      x: drag.overflowX ? clampCropValue(drag.x - (deltaX / drag.overflowX) * 100) : 50,
      y: drag.overflowY ? clampCropValue(drag.y - (deltaY / drag.overflowY) * 100) : 50,
    });
  };

  return (
    <div className="reading-plan-cover-cropper">
      <div className="reading-plan-cover-crop-frame">
        <canvas
          ref={canvasRef}
          width="960"
          height="300"
          onPointerDown={beginDrag}
          onPointerMove={dragImage}
          onPointerUp={() => { dragRef.current = null; }}
          onPointerCancel={() => { dragRef.current = null; }}
          aria-label={translate('拖動圖片選擇封面顯示範圍')}
        />
        <span>{translate('拖動圖片調整顯示範圍')}</span>
      </div>
      <label>
        <span>{translate('縮放')}</span>
        <input
          type="range"
          min="1"
          max="3"
          step="0.01"
          value={crop.zoom}
          onChange={event => onChange({ ...crop, zoom: Number(event.target.value) })}
        />
      </label>
      <div className="reading-plan-cover-crop-position">
        <label>
          <span>{translate('水平位置')}</span>
          <input type="range" min="0" max="100" value={crop.x} onChange={event => onChange({ ...crop, x: Number(event.target.value) })} />
        </label>
        <label>
          <span>{translate('垂直位置')}</span>
          <input type="range" min="0" max="100" value={crop.y} onChange={event => onChange({ ...crop, y: Number(event.target.value) })} />
        </label>
      </div>
    </div>
  );
}

const createEmptyPlanDay = dayNumber => ({
  id: null,
  scriptureReferences: '',
  title: `第 ${dayNumber} 天`,
  summary: '',
});

const createEmptyPlanForm = () => ({
  name: '',
  description: '',
  category: '靈修',
  coverImageUrl: '',
  visibility: 'shareable',
  startMode: 'individual',
  fixedStartDate: '',
  days: [createEmptyPlanDay(1)],
  templateId: '',
});

const createEmptyResourceForm = () => ({
  resourceType: 'commentary',
  title: '',
  link: '',
  sourceName: '',
  description: '',
  resourceId: '',
  engineId: '',
  sortOrder: 0,
  isSharedWithParticipants: getShareResourcesWithParticipantsDefault(),
});

const resourceTypeLabels = {
  audio: '朗讀',
  commentary: '注釋',
  explanation: '講解',
  sermon: '證道',
  video: '影片',
  article: '文章',
  scripture: '經文',
  song: '詩歌',
  web: '網路',
  prayer: '禱告',
  outline: '綱目',
  illustration: '例證',
  note: '讀經筆記',
};

const searchResourceType = doctype => ({
  註解: 'commentary',
  拾穗: 'explanation',
  綱目: 'explanation',
  例證: 'explanation',
  信息: 'sermon',
  網路: 'article',
  詩歌: 'video',
}[doctype] || 'article');

function ReadingPlansPage() {
  const navigate = useNavigate();
  const { shareCode } = useParams();
  const isAndroidApp = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
  const pageQuery = new URLSearchParams(window.location.search);
  const preferredPlanId = Number(pageQuery.get('planId')) || null;
  const preferredDayId = Number(pageQuery.get('dayId')) || null;

  const language = localStorage.userLanguage || 'T';
  const translate = text => TranslateLanguage(language, text);
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem('user'));
    } catch {
      return null;
    }
  }, []);
  const isAdmin = useMemo(() => {
    try {
      const token = sessionStorage.getItem('token');
      return Boolean(token && jwtDecode(token)?.role === 'Admin');
    } catch {
      return false;
    }
  }, []);

  const [plans, setPlans] = useState([]);
  const [planTemplates, setPlanTemplates] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverCrop, setCoverCrop] = useState(null);
  const [coverCropError, setCoverCropError] = useState('');
  const coverFileInputRef = useRef(null);
  const [message, setMessage] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(!currentUser);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [resourceModalMode, setResourceModalMode] = useState('resource');
  const [editingResource, setEditingResource] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showResourceSearchModal, setShowResourceSearchModal] = useState(false);
  const [planForm, setPlanForm] = useState(createEmptyPlanForm);
  const [resourceForm, setResourceForm] = useState(createEmptyResourceForm);
  const [joinStartDate, setJoinStartDate] = useState(todayValue());
  const [scriptureValidation, setScriptureValidation] = useState({});
  const [scriptureFlyout, setScriptureFlyout] = useState(null);
  const [resourceSearchTerm, setResourceSearchTerm] = useState('');
  const [resourceSearchResults, setResourceSearchResults] = useState([]);
  const [resourceSearchLoading, setResourceSearchLoading] = useState(false);
  const [playingResource, setPlayingResource] = useState(null);
  const [resourceActionMenuId, setResourceActionMenuId] = useState(null);
  const [expandedResourceIds, setExpandedResourceIds] = useState([]);
  const [shareLink, setShareLink] = useState('');
  const [activePlanSection, setActivePlanSection] = useState('daily');
  const [showCompletedPlans, setShowCompletedPlans] = useState(false);
  const [showArchivedPlans, setShowArchivedPlans] = useState(false);
  const [showOverduePlans, setShowOverduePlans] = useState(false);
  const [showPlanLibrary, setShowPlanLibrary] = useState(false);
  const [showPlanDetails, setShowPlanDetails] = useState(false);
  const [showDayNavigation, setShowDayNavigation] = useState(false);
  const [showCheckInCalendar, setShowCheckInCalendar] = useState(false);
  const [showFocusMenu, setShowFocusMenu] = useState(false);
  const [showRestartModal, setShowRestartModal] = useState(false);
  const [restartStartDate, setRestartStartDate] = useState(todayValue);
  const [reminderSendingKey, setReminderSendingKey] = useState('');

  const selectedDay = selectedPlan?.days?.[selectedDayIndex] || null;
  const selectedPlanTiming = getPlanTiming(selectedPlan);
  const selectedDayDate = planDayDate(selectedPlanTiming.startDate, selectedDay?.dayNumber);
  const todayPlanDayIndex = selectedPlanTiming.elapsedDays >= 1
    && selectedPlanTiming.elapsedDays <= (selectedPlan?.days?.length || 0)
    ? selectedPlan.days.findIndex(day => Number(day.dayNumber) === selectedPlanTiming.elapsedDays)
    : -1;
  useEffect(() => {
    setShowCheckInCalendar(false);
  }, [selectedPlan?.id]);
  const progressValue = selectedPlan?.durationDays
    ? Math.round(((selectedPlan.completedDays || 0) / selectedPlan.durationDays) * 100)
    : 0;
  const displayedCoverForPlan = plan => (
    showCreateModal && editingPlan?.id === plan?.id
      ? planForm.coverImageUrl
      : plan?.coverImageUrl
  );
  const ongoingPlans = useMemo(
    () => plans.filter(plan => !plan.isArchived && !isPlanCompleted(plan) && !getPlanTiming(plan).isOverdue),
    [plans]
  );
  const overduePlans = useMemo(
    () => plans.filter(plan => !plan.isArchived && !isPlanCompleted(plan) && getPlanTiming(plan).isOverdue),
    [plans]
  );
  const completedPlans = useMemo(
    () => plans.filter(plan => !plan.isArchived && isPlanCompleted(plan)),
    [plans]
  );
  const archivedPlans = useMemo(
    () => plans.filter(plan => plan.isArchived),
    [plans]
  );

  useEffect(() => {
    if (selectedPlan?.isArchived) setShowArchivedPlans(true);
    else if (isPlanCompleted(selectedPlan)) setShowCompletedPlans(true);
  }, [selectedPlan?.id, selectedPlan?.isArchived, selectedPlan?.completedDays, selectedPlan?.durationDays]);

  const reportError = error => {
    const text = error?.response?.data?.message || error?.message || translate('操作失敗，請稍後再試。');
    setMessage({ color: 'danger', text });
  };

  const loadPlanDetails = async id => {
    const endpoint = shareCode
      ? `/api/readingplans/shared/${shareCode}`
      : `/api/readingplans/${id}`;
    const { data } = await axios.get(endpoint, withClientDate({ params: { _ts: Date.now() } }));
    const normalizedPlan = normalizePlan(data);
    setSelectedPlan(normalizedPlan);
    if (!shareCode && isOngoingPlan(normalizedPlan)) {
      setLastOpenedPlanId(currentUser?.id, normalizedPlan.id);
    }
    setSelectedDayIndex(previous => {
      const requestedDayIndex = preferredPlanId === normalizedPlan.id && preferredDayId
        ? (normalizedPlan.days || []).findIndex(day => day.id === preferredDayId)
        : -1;
      if (requestedDayIndex >= 0) return requestedDayIndex;
      const lastIndex = Math.max((normalizedPlan.days?.length || 1) - 1, 0);
      if (selectedPlan?.id !== normalizedPlan.id) return Math.min(Math.max((normalizedPlan.myCurrentDayNumber || 1) - 1, 0), lastIndex);
      return Math.min(previous, lastIndex);
    });
    return normalizedPlan;
  };

  const loadPlanTemplates = async () => {
    try {
      const { data } = await axios.get('/api/readingplans/templates');
      setPlanTemplates(data || []);
    } catch (error) {
      reportError(error);
    }
  };

  const loadPlans = async preferredId => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/readingplans', withClientDate({ params: { _ts: Date.now() } }));
      const normalizedPlans = (data || []).map(normalizePlan);
      setPlans(normalizedPlans);
      const defaultPlan = defaultPlanFrom(normalizedPlans);
      const rememberedId = getLastOpenedPlanId(currentUser?.id);
      const rememberedPlan = normalizedPlans.find(plan => plan.id === rememberedId && isOngoingPlan(plan));
      if (rememberedId && !rememberedPlan) clearLastOpenedPlanId(currentUser?.id);
      const requestedId = preferredId === null
        ? defaultPlan?.id
        : preferredId || selectedPlan?.id || rememberedPlan?.id || defaultPlan?.id;
      const nextId = normalizedPlans.some(plan => plan.id === requestedId) ? requestedId : defaultPlan?.id;
      if (nextId) await loadPlanDetails(nextId);
      else setSelectedPlan(null);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  };

  const loadSharedPlan = async code => {
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/readingplans/shared/${code}`, withClientDate({ params: { _ts: Date.now() } }));
      const normalizedPlan = normalizePlan(data);
      setPlans([normalizedPlan]);
      setSelectedPlan(normalizedPlan);
      setSelectedDayIndex(Math.max((normalizedPlan.myCurrentDayNumber || 1) - 1, 0));
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      setShowLoginModal(true);
      return;
    }
    loadPlanTemplates();
    if (shareCode) loadSharedPlan(shareCode);
    else loadPlans(preferredPlanId || undefined);
  }, [shareCode, currentUser]);

  const openMyReadingPlans = () => {
    setSelectedPlan(null);
    setSelectedDayIndex(0);
    navigate('/reading-plans');
  };

  const requireLogin = action => {
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }
    action();
  };

  const closeJoinModal = () => {
    setShowJoinModal(false);
    setMessage(null);
  };

  const joinPlan = () => requireLogin(() => {
    setMessage(null);
    setJoinStartDate(selectedPlan?.startMode === 'synchronized' && selectedPlan?.fixedStartDate
      ? String(selectedPlan.fixedStartDate).slice(0, 10)
      : todayValue());
    setShowJoinModal(true);
  });

  const confirmJoinPlan = async () => {
    setBusy(true);
    try {
      await axios.post(
        `/api/readingplans/${selectedPlan.id}/join`,
        selectedPlan.startMode === 'synchronized' ? {} : { startDate: joinStartDate },
        withClientDate()
      );
      setShowJoinModal(false);
      setMessage({ color: 'success', text: translate('已加入讀經計劃。') });
      if (shareCode) await loadSharedPlan(shareCode);
      else await loadPlans(selectedPlan.id);
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const openLeaveModal = () => {
    if (!selectedPlan?.isJoined || selectedPlan?.isOwner) return;
    setMessage(null);
    setShowLeaveModal(true);
  };

  const closeLeaveModal = () => {
    setShowLeaveModal(false);
    setMessage(null);
  };

  const leavePlan = async () => {
    setBusy(true);
    try {
      await axios.delete(`/api/readingplans/${selectedPlan.id}/join`);
      setShowLeaveModal(false);
      if (shareCode) {
        await loadSharedPlan(shareCode);
      } else {
        setSelectedPlan(null);
        await loadPlans(null);
      }
      setMessage({ color: 'success', text: translate('已退出讀經計劃；原有打卡記錄已保留。') });
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const toggleCheckIn = () => requireLogin(async () => {
    if (!selectedPlan?.isJoined) {
      setMessage({ color: 'warning', text: translate('請先加入這個讀經計劃。') });
      return;
    }
    setBusy(true);
    try {
      await axios.put(
        `/api/readingplans/${selectedPlan.id}/days/${selectedDay.id}/check-in`,
        { completed: !selectedDay.completed },
        withClientDate()
      );
      const refreshed = shareCode
        ? (await axios.get(`/api/readingplans/shared/${shareCode}`, withClientDate())).data
        : await loadPlanDetails(selectedPlan.id);
      if (shareCode) setSelectedPlan(refreshed);
      setMessage({
        color: 'success',
        text: selectedDay.completed ? translate('已取消本日打卡。') : translate('本日打卡成功！'),
      });
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  });

  const sharePlan = () => requireLogin(async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`/api/readingplans/${selectedPlan.id}/share`);
      const fullUrl = new URL(data.shareLink, window.location.origin).toString();
      setShareLink(fullUrl);
    } catch (error) {
      if (error?.name !== 'AbortError') reportError(error);
    } finally {
      setBusy(false);
    }
  });

  const copyPlanShareLink = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareLink);
      } else {
        const input = document.createElement('textarea');
        input.value = shareLink;
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        input.remove();
      }
      setMessage({ color: 'success', text: translate('分享鏈接已複製。') });
    } catch (error) {
      reportError(error);
    }
  };

  const openNativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({ title: selectedPlan?.name || translate('讀經計劃'), text: selectedPlan?.description || '', url: shareLink });
    } catch (error) {
      if (error?.name !== 'AbortError') reportError(error);
    }
  };

  const closePlanEditor = () => {
    setCoverCrop(current => {
      if (current?.objectUrl) URL.revokeObjectURL(current.objectUrl);
      return null;
    });
    setShowCreateModal(false);
    setEditingPlan(null);
    setEditingTemplate(null);
    setPlanForm(createEmptyPlanForm());
    setScriptureValidation({});
    setMessage(null);
  };

  const openCreatePlan = () => {
    setEditingPlan(null);
    setEditingTemplate(null);
    setPlanForm(createEmptyPlanForm());
    setScriptureValidation({});
    setMessage(null);
    setShowCreateModal(true);
  };

  const openEditPlan = () => {
    if (!selectedPlan?.isOwner) return;
    const days = (selectedPlan.days || []).map(day => ({
      id: day.id,
      scriptureReferences: day.scriptureReferences || '',
      title: day.title || '',
      summary: day.summary || '',
    }));
    setEditingPlan(selectedPlan);
    setPlanForm({
      name: selectedPlan.name || '',
      description: selectedPlan.description || '',
      category: selectedPlan.category || '',
      coverImageUrl: selectedPlan.coverImageUrl || '',
      visibility: selectedPlan.visibility || 'shareable',
      startMode: selectedPlan.startMode || 'individual',
      fixedStartDate: selectedPlan.fixedStartDate ? String(selectedPlan.fixedStartDate).slice(0, 10) : '',
      days: days.length ? days : [createEmptyPlanDay(1)],
      templateId: '',
    });
    setScriptureValidation({});
    setMessage(null);
    setShowCreateModal(true);
  };

  const getTemplateDetails = async template => {
    if (template?.days?.length) return template;
    const { data } = await axios.get(`/api/readingplans/templates/${template.id}`);
    return data;
  };

  const openEditTemplate = async template => {
    if (!isAdmin) return;
    setBusy(true);
    try {
      const details = await getTemplateDetails(template);
      setEditingPlan(null);
      setEditingTemplate(details);
      setPlanForm({
        name: details.name || '',
        description: details.description || '',
        category: details.category || '讀經計劃',
        coverImageUrl: '',
        visibility: 'shareable',
        startMode: 'individual',
        fixedStartDate: '',
        templateId: details.id,
        days: (details.days || []).map(day => ({
          id: null,
          scriptureReferences: day.scriptureReferences || '',
          title: day.title || '',
          summary: day.summary || '',
        })),
      });
      setScriptureValidation({});
      setMessage(null);
      setShowCreateModal(true);
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const applyTemplate = async template => {
    setBusy(true);
    try {
      const details = await getTemplateDetails(template);
      setPlanForm(current => ({
        ...current,
        templateId: details.id,
        name: details.name,
        description: details.description,
        category: details.category,
        days: (details.days || []).map(day => ({
          id: null,
          scriptureReferences: day.scriptureReferences || '',
          title: day.title || '',
          summary: day.summary || '',
        })),
      }));
      setScriptureValidation({});
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const updatePlanDay = (index, field, value) => {
    if (editingPlan?.hasOtherParticipants && field === 'scriptureReferences' && planForm.days[index]?.id) {
      setMessage({ color: 'warning', text: translate('已有其他人參與後，不能修改每日經文範圍。') });
      return;
    }
    setPlanForm(current => ({
      ...current,
      templateId: '',
      days: current.days.map((day, dayIndex) => dayIndex === index ? { ...day, [field]: value } : day),
    }));
    if (field === 'scriptureReferences') {
      setScriptureValidation(current => {
        const next = { ...current };
        delete next[index];
        return next;
      });
    }
  };

  const addPlanDay = () => {
    if (editingPlan?.hasOtherParticipants) {
      setMessage({ color: 'warning', text: translate('已有其他人參與後，不能增加計劃天數。') });
      return;
    }
    setPlanForm(current => ({
      ...current,
      templateId: '',
      days: [...current.days, createEmptyPlanDay(current.days.length + 1)],
    }));
  };

  const removePlanDay = index => {
    if (planForm.days.length <= 1) {
      setMessage({ color: 'warning', text: translate('讀經計劃至少需要一天。') });
      return;
    }
    setPlanForm(current => ({
      ...current,
      templateId: '',
      days: current.days.filter((_, dayIndex) => dayIndex !== index),
    }));
    setScriptureValidation({});
  };

  const validateDayScripture = async index => {
    const reference = String(planForm.days[index]?.scriptureReferences || '').trim();
    if (!reference) {
      setScriptureValidation(current => ({
        ...current,
        [index]: { status: 'invalid', isValid: false, message: translate('請填寫經文範圍。') },
      }));
      return;
    }

    setScriptureValidation(current => ({ ...current, [index]: { status: 'checking' } }));
    try {
      const { data } = await axios.post('/api/readingplans/validate-scriptures', { references: [reference] });
      const item = data.items?.[0];
      setScriptureValidation(current => ({
        ...current,
        [index]: {
          status: item?.isValid ? 'valid' : 'invalid',
          isValid: Boolean(item?.isValid),
          normalized: item?.normalized || '',
          message: item?.message || '',
        },
      }));
      if (item?.isValid && item.normalized) {
        setPlanForm(current => ({
          ...current,
          days: current.days.map((day, dayIndex) => (
            dayIndex === index && day.scriptureReferences.trim() === reference
              ? { ...day, scriptureReferences: item.normalized }
              : day
          )),
        }));
      }
    } catch {
      setScriptureValidation(current => ({
        ...current,
        [index]: { status: 'invalid', isValid: false, message: translate('經文檢查失敗，請稍後再試。') },
      }));
    }
  };

  const validatePlanDays = async days => {
    const { data } = await axios.post('/api/readingplans/validate-scriptures', {
      references: days.map(day => day.scriptureReferences),
    });
    const nextValidation = {};
    (data.items || []).forEach((item, index) => {
      nextValidation[index] = {
        status: item.isValid ? 'valid' : 'invalid',
        isValid: item.isValid,
        normalized: item.normalized || '',
        message: item.message || '',
      };
    });
    setScriptureValidation(nextValidation);
    if (!data.valid) return null;
    return days.map((day, index) => ({
      ...day,
      scriptureReferences: data.items[index].normalized,
    }));
  };

  const selectPlanCover = event => {
    const [file] = Array.from(event.target.files || []);
    event.target.value = '';
    if (!file) return;
    setCoverCropError('');
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ color: 'danger', text: translate('圖片不可超過 5 MB。') });
      return;
    }
    if (!isSupportedCoverFile(file)) {
      setMessage({ color: 'danger', text: translate('請選擇手機相冊中的圖片檔案。') });
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      setCoverCrop(current => {
        if (current?.objectUrl) URL.revokeObjectURL(current.objectUrl);
        return { file, objectUrl, image, x: 50, y: 50, zoom: 1 };
      });
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      setMessage({ color: 'danger', text: translate('無法讀取這張圖片，請嘗試選擇 JPG 或 PNG 圖片。') });
    };
    image.src = objectUrl;
  };

  const openPlanCoverPicker = () => {
    const input = coverFileInputRef.current;
    if (!input || coverUploading) return;
    setCoverCropError('');
    setMessage(null);
    try {
      if (typeof input.showPicker === 'function') input.showPicker();
      else input.click();
    } catch {
      input.click();
    }
  };

  const closeCoverCrop = () => {
    setCoverCropError('');
    setCoverCrop(current => {
      if (current?.objectUrl) URL.revokeObjectURL(current.objectUrl);
      return null;
    });
  };

  const uploadPlanCover = async () => {
    if (!coverCrop) return;
    const formData = new FormData();
    setCoverUploading(true);
    setCoverCropError('');
    try {
      const croppedBlob = await createCroppedCoverBlob(coverCrop);
      formData.append('file', croppedBlob, 'reading-plan-cover.jpg');
      const { data } = await axios.post('/api/readingplans/cover-image', formData);
      setPlanForm(current => ({ ...current, coverImageUrl: data.url }));
      setMessage({ color: 'success', text: translate('封面圖片已上傳。保存計劃後即可使用。') });
      closeCoverCrop();
    } catch (error) {
      const errorMessage = error?.response?.data?.message
        || error?.message
        || translate('封面圖片上傳失敗，請檢查網絡後重試。');
      setCoverCropError(errorMessage);
    } finally {
      setCoverUploading(false);
    }
  };

  const savePlan = async () => {
    const days = planForm.days.map((day, index) => ({
      id: day.id || null,
      scriptureReferences: String(day.scriptureReferences || '').trim(),
      title: String(day.title || '').trim() || `${translate('第')} ${index + 1} ${translate('天')}`,
      summary: String(day.summary || '').trim(),
    }));
    if (!planForm.name.trim() || !days.length || days.some(day => !day.scriptureReferences)) {
      const validation = {};
      days.forEach((day, index) => {
        if (!day.scriptureReferences) validation[index] = { status: 'invalid', isValid: false, message: translate('請填寫經文範圍。') };
      });
      setScriptureValidation(validation);
      setMessage({ color: 'warning', text: translate('請填寫計劃名稱和每日經文範圍。') });
      return;
    }
    if (planForm.startMode === 'synchronized' && !planForm.fixedStartDate) {
      setMessage({ color: 'warning', text: translate('請選擇同步進度的統一起始日。') });
      return;
    }

    setBusy(true);
    try {
      const validatedDays = await validatePlanDays(days);
      if (!validatedDays) {
        setMessage({ color: 'warning', text: translate('部分經文範圍不正確，請按提示修改。') });
        return;
      }
      const payload = {
        name: planForm.name,
        description: planForm.description,
        category: planForm.category,
        coverImageUrl: planForm.coverImageUrl,
        visibility: planForm.visibility,
        startMode: planForm.startMode,
        fixedStartDate: planForm.startMode === 'synchronized' ? planForm.fixedStartDate : null,
        days: validatedDays,
      };
      if (editingTemplate) {
        await axios.put(`/api/readingplans/templates/${editingTemplate.id}`, {
          name: payload.name,
          description: payload.description,
          category: payload.category,
          days: payload.days,
        });
        closePlanEditor();
        await loadPlanTemplates();
        setMessage({ color: 'success', text: translate('讀經模板已更新。') });
        return;
      }
      const wasEditing = Boolean(editingPlan);
      const targetPlanId = editingPlan?.id;
      const { data } = wasEditing
        ? await axios.put(`/api/readingplans/${targetPlanId}`, payload, withClientDate())
        : await axios.post('/api/readingplans', payload, withClientDate());
      if (wasEditing) {
        const updatedPlan = normalizePlan(data);
        setPlans(current => current.map(plan => plan.id === targetPlanId
          ? { ...plan, ...updatedPlan }
          : plan));
        setSelectedPlan(updatedPlan);
        closePlanEditor();
        setMessage({ color: 'success', text: translate('讀經計劃已更新。') });
        return;
      }
      closePlanEditor();
      setMessage({ color: 'success', text: translate('讀經計劃已建立。') });
      if (shareCode) await loadSharedPlan(shareCode);
      else await loadPlans(data.id);
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };
  const saveResource = async () => {
    const isNote = resourceForm.resourceType === 'note';
    if (!resourceForm.title.trim()) {
      setMessage({ color: 'warning', text: translate('請填寫標題。') });
      return;
    }
    if (isNote && !resourceForm.description.trim()) {
      setMessage({ color: 'warning', text: translate('請填寫讀經筆記內容。') });
      return;
    }
    if (!isNote && (!resourceForm.title.trim() || !resourceForm.link.trim())) {
      setMessage({ color: 'warning', text: translate('請填寫資源標題和鏈接。') });
      return;
    }
    setBusy(true);
    try {
      if (editingResource) {
        await axios.put(
          `/api/readingplans/${selectedPlan.id}/resources/${editingResource.id}`,
          resourceForm
        );
      } else {
        await axios.post(
          `/api/readingplans/${selectedPlan.id}/days/${selectedDay.id}/resources`,
          resourceForm
        );
      }
      setResourceForm(createEmptyResourceForm());
      setEditingResource(null);
      setShowResourceModal(false);
      await loadPlanDetails(selectedPlan.id);
      setMessage({ color: 'success', text: translate(editingResource ? '內容已更新。' : (isNote ? '讀經筆記已加入。' : '相關內容已加入。')) });
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const closeResourceEditor = () => {
    setShowResourceModal(false);
    setEditingResource(null);
    setResourceForm(createEmptyResourceForm());
    setMessage(null);
  };

  const openManualResource = () => {
    setMessage(null);
    setEditingResource(null);
    setResourceModalMode('resource');
    setResourceForm(createEmptyResourceForm());
    setShowResourceModal(true);
  };

  const openReadingNote = () => {
    setMessage(null);
    setEditingResource(null);
    setResourceModalMode('note');
    setResourceForm({
      ...createEmptyResourceForm(),
      resourceType: 'note',
      title: translate('讀經筆記'),
    });
    setShowResourceModal(true);
  };

  const openEditResource = resource => {
    setMessage(null);
    setEditingResource(resource);
    setResourceModalMode(resource.resourceType === 'note' ? 'note' : 'resource');
    setResourceForm({
      resourceType: resource.resourceType || 'article',
      title: resource.title || '',
      link: resource.link || '',
      sourceName: resource.sourceName || '',
      description: resource.description || '',
      resourceId: resource.resourceId || '',
      engineId: resource.engineId || '',
      sortOrder: resource.sortOrder || 0,
      isSharedWithParticipants: Boolean(resource.isSharedWithParticipants),
    });
    setShowResourceModal(true);
  };

  const deleteResource = async resource => {
    if (!window.confirm(translate('確定要刪除這項內容嗎？'))) return;
    setBusy(true);
    try {
      await axios.delete(`/api/readingplans/${selectedPlan.id}/resources/${resource.id}`);
      await loadPlanDetails(selectedPlan.id);
      setMessage({ color: 'success', text: translate('內容已刪除。') });
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };
  const openScripture = selectedReference => {
    const reference = selectedReference === undefined
      ? parseReadingPlanReference(selectedDay?.scriptureReferences)
      : selectedReference;
    if (!reference) {
      setMessage({ color: 'warning', text: translate('無法識別這個經文範圍。') });
      return;
    }
    setScriptureFlyout({
      ...reference,
      selectedVerse: reference.verseStart || 1,
    });
  };

  const searchResources = async term => {
    const query = String(term || '').trim();
    if (!query) return;
    setResourceSearchLoading(true);
    try {
      const response = await resourceSearchConnector.onSearch(
        { searchTerm: query, current: 1, resultsPerPage: 20 },
        { ...resourceSearchQueryConfig, resultsPerPage: 20 }
      );
      setResourceSearchResults((response.results || []).filter(result => resultValue(result, 'doctype') !== '經文'));
    } catch (error) {
      reportError(error);
    } finally {
      setResourceSearchLoading(false);
    }
  };

  const openResourceSearch = queryOverride => {
    const query = typeof queryOverride === 'string'
      ? queryOverride
      : (selectedDay?.scriptureReferences || '');
    const searchParams = new URLSearchParams({
      q: query,
      readingPlanId: String(selectedPlan.id),
      readingPlanDayId: String(selectedDay.id),
    });
    window.location.assign(`/?${searchParams.toString()}`);
  };

  const addSearchResource = async result => {
    const doctype = resultValue(result, 'doctype');
    const title = resultValue(result, 'title') || translate('相關內容');
    const link = resultValue(result, 'link') || `/?q=${encodeURIComponent(title)}`;
    setBusy(true);
    try {
      await axios.post(`/api/readingplans/${selectedPlan.id}/days/${selectedDay.id}/resources`, {
        resourceType: searchResourceType(doctype),
        title,
        description: resultValue(result, 'description') || resultValue(result, 'source'),
        link,
        sourceName: resultValue(result, 'publisher') || resultValue(result, 'source') || doctype,
        resourceId: resultValue(result, 'id'),
        engineId: engineName,
      });
      await loadPlanDetails(selectedPlan.id);
      setMessage({ color: 'success', text: translate('已從搜尋結果加入相關內容。') });
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const closeResourceSearchModal = () => {
    setShowResourceSearchModal(false);
    setMessage(null);
  };

  const openDeleteModal = () => {
    setMessage(null);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setMessage(null);
  };
  const deleteOrArchivePlan = async () => {
    setBusy(true);
    try {
      const { data } = await axios.delete(`/api/readingplans/${selectedPlan.id}`);
      setShowDeleteModal(false);
      if (data.archived) {
        setMessage({ color: 'warning', text: translate('計劃已有分享或參與記錄，已改為封存並保留所有人的進度。') });
        await loadPlans(selectedPlan.id);
      } else {
        setSelectedPlan(null);
        setMessage({ color: 'success', text: translate('讀經計劃已刪除。') });
        await loadPlans(null);
      }
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const restorePlan = async () => {
    setBusy(true);
    try {
      await axios.post(`/api/readingplans/${selectedPlan.id}/restore`);
      setMessage({ color: 'success', text: translate('讀經計劃已恢復，可繼續參與和分享。') });
      await loadPlans(selectedPlan.id);
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const openRestartModal = () => {
    setRestartStartDate(todayValue());
    setMessage(null);
    setShowRestartModal(true);
  };

  const restartPlan = async () => {
    if (!restartStartDate) {
      setMessage({ color: 'warning', text: translate('請選擇新的開始日期。') });
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `/api/readingplans/${selectedPlan.id}/restart`,
        { startDate: restartStartDate },
        withClientDate()
      );
      setShowRestartModal(false);
      setMessage({ color: 'success', text: translate('計劃已重新開始，舊有打卡和筆記仍然保留。') });
      await loadPlans(selectedPlan.id);
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const sendCheckInReminder = async participant => {
    if (!selectedDay) return;
    const reminderKey = `${selectedDay.id}-${participant.userId}`;
    setReminderSendingKey(reminderKey);
    try {
      const { data } = await axios.post(`/api/readingplans/${selectedPlan.id}/reminders`, {
        dayId: selectedDay.id,
        userId: participant.userId,
      }, withClientDate());
      setSelectedPlan(current => ({
        ...current,
        recentReminders: [
          ...(current.recentReminders || []),
          { readingPlanDayId: selectedDay.id, recipientUserId: participant.userId, sentAt: data.sentAt },
        ],
      }));
      setMessage({ color: 'success', text: translate(`已提醒 ${participant.name || '參與者'}。`) });
    } catch (error) {
      reportError(error);
    } finally {
      setReminderSendingKey('');
    }
  };

  const hasOpenActionModal = showCreateModal
    || showResourceModal
    || showJoinModal
    || showLeaveModal
    || showResourceSearchModal
    || showDeleteModal
    || showRestartModal
    || Boolean(playingResource);
  const renderPlanCard = plan => {
    const displayedCover = displayedCoverForPlan(plan);
    const isCompleted = isPlanCompleted(plan);
    const timing = getPlanTiming(plan);
    const statusKey = plan.isArchived ? 'archived' : (isCompleted ? 'completed' : (timing.isOverdue ? 'overdue' : (timing.isUpcoming ? 'upcoming' : 'active')));
    const statusLabel = plan.isArchived ? '已封存' : (isCompleted ? '已完成' : (timing.isOverdue ? '已到期' : (timing.isUpcoming ? '尚未開始' : '進行中')));
    return (
      <button
        key={plan.id}
        type="button"
        className={`reading-plan-card${selectedPlan?.id === plan.id ? ' is-selected' : ''}${plan.isArchived ? ' is-archived' : ''}${isCompleted && !plan.isArchived ? ' is-completed' : ''}${safeCoverImageUrl(displayedCover) ? ' has-cover' : ''}`}
        style={coverBackgroundStyle(displayedCover)}
        onClick={() => {
          setActivePlanSection('daily');
          setShowPlanLibrary(false);
          setShowPlanDetails(false);
          setShowDayNavigation(false);
          loadPlanDetails(plan.id);
        }}
      >
        <span className="reading-plan-card-heading">
          <strong>{translate(plan.name)}</strong>
          <span className={`reading-plan-card-status is-${statusKey}`}>{translate(statusLabel)}</span>
        </span>
        <span>{plan.durationDays} {translate('天')} · {plan.participantCount} {translate('人參與')}</span>
        <small>{translate(plan.category || '讀經計劃')} · {translate(plan.startMode === 'synchronized' ? '同步日期進度' : '各自起算')}</small>
      </button>
    );
  };
  const renderModalMessage = () => message ? (
    <div className="reading-plan-modal-message">
      <EuiCallOut color={message.color} title={message.text} onDismiss={() => setMessage(null)} />
    </div>
  ) : null;
  if (!currentUser) {
    return (
      <LoginRequiredModal
        isOpen={showLoginModal}
        onClose={() => {
          setShowLoginModal(false);
          navigate('/');
        }}
        nextUrl={typeof window !== 'undefined' ? window.location.href : '/'}
        title={translate('請先登入')}
        message={translate('登入後才可以建立、參與和分享讀經計劃；若還不是用戶，請先註冊。')}
      />
    );
  }

  return (
    <div className={`reading-plans-page${scriptureFlyout ? ' has-scripture-flyout' : ''}`}>
      <header className="reading-plans-header">
        <EuiTitle size="m"><h1>{translate('讀經計劃')}</h1></EuiTitle>
        <div className="reading-plans-header-actions">
          <EuiButtonEmpty
            className="reading-plans-home-link"
            size="s"
            iconType="home"
            color="text"
            onClick={() => window.location.replace('/')}
          >
            {translate('首頁')}
          </EuiButtonEmpty>
          <LanguageToggle />
          <TopRightMenuAvatar withLanguage="1" />
        </div>
      </header>

      <EuiGlobalToastList
        className="reading-plans-toast-list"
        toasts={message && !hasOpenActionModal ? [{
          id: `${message.color}-${message.text}`,
          title: message.text,
          color: message.color,
          iconType: message.color === 'success'
            ? 'checkInCircleFilled'
            : (message.color === 'danger' ? 'errorFilled' : 'warning'),
        }] : []}
        dismissToast={() => setMessage(null)}
        toastLifeTimeMs={6000}
      />

      {loading ? (
        <div className="reading-plans-loading"><EuiLoadingSpinner size="xl" /></div>
      ) : (
        <main className={`reading-plans-layout${showPlanLibrary ? ' is-library-open' : ' is-focus-mode'}`}>
          {showPlanLibrary && (
          <aside className="reading-plans-sidebar">
            <div className="reading-plans-sidebar-title">
              <EuiTitle size="s"><h2>{translate(shareCode ? '分享的計劃' : '我的讀經計劃')}</h2></EuiTitle>
              <div className="reading-plans-sidebar-actions">
                {!shareCode && currentUser && (
                  <EuiButton size="s" onClick={openCreatePlan}>
                    <span aria-hidden="true">＋</span>&nbsp;{translate('建立')}
                  </EuiButton>
                )}
                <EuiButtonIcon
                  iconType="cross"
                  display="empty"
                  aria-label={translate('收起計劃列表')}
                  onClick={() => setShowPlanLibrary(false)}
                />
              </div>
            </div>
            {!plans.length && (
              <EuiText size="s"><p>{translate('目前還沒有讀經計劃。')}</p></EuiText>
            )}
            <div className="reading-plan-groups">
              <section className="reading-plan-group is-active-group">
                <div className="reading-plan-group-title">
                  <strong>{translate('進行中的計劃')}</strong>
                  <span>{ongoingPlans.length}</span>
                </div>
                {ongoingPlans.length ? (
                  <div className="reading-plan-card-list">{ongoingPlans.map(renderPlanCard)}</div>
                ) : (
                  <p className="reading-plan-group-empty">{translate('目前沒有進行中的計劃。')}</p>
                )}
              </section>

              {overduePlans.length > 0 && (
                <section className="reading-plan-group">
                  <button
                    type="button"
                    className="reading-plan-group-toggle"
                    aria-expanded={showOverduePlans}
                    onClick={() => setShowOverduePlans(value => !value)}
                  >
                    <span><strong>{translate('已到期未完成')}</strong><small>{overduePlans.length}</small></span>
                    <EuiIcon type={showOverduePlans ? 'arrowDown' : 'arrowRight'} />
                  </button>
                  {showOverduePlans && (
                    <div className="reading-plan-card-list">{overduePlans.map(renderPlanCard)}</div>
                  )}
                </section>
              )}

              {completedPlans.length > 0 && (
                <section className="reading-plan-group">
                  <button
                    type="button"
                    className="reading-plan-group-toggle"
                    aria-expanded={showCompletedPlans}
                    onClick={() => setShowCompletedPlans(value => !value)}
                  >
                    <span><strong>{translate('已完成的計劃')}</strong><small>{completedPlans.length}</small></span>
                    <EuiIcon type={showCompletedPlans ? 'arrowDown' : 'arrowRight'} />
                  </button>
                  {showCompletedPlans && (
                    <div className="reading-plan-card-list">{completedPlans.map(renderPlanCard)}</div>
                  )}
                </section>
              )}

              {archivedPlans.length > 0 && (
                <section className="reading-plan-group">
                  <button
                    type="button"
                    className="reading-plan-group-toggle"
                    aria-expanded={showArchivedPlans}
                    onClick={() => setShowArchivedPlans(value => !value)}
                  >
                    <span><strong>{translate('已封存的計劃')}</strong><small>{archivedPlans.length}</small></span>
                    <EuiIcon type={showArchivedPlans ? 'arrowDown' : 'arrowRight'} />
                  </button>
                  {showArchivedPlans && (
                    <div className="reading-plan-card-list">{archivedPlans.map(renderPlanCard)}</div>
                  )}
                </section>
              )}
            </div>
            {selectedPlan && (
              <nav className="reading-plan-view-navigation" aria-label={translate('讀經計劃內容')}>
                <button
                  type="button"
                  className={activePlanSection === 'daily' ? 'is-active' : ''}
                  onClick={() => setActivePlanSection('daily')}
                >
                  <EuiIcon type="reporter" />
                  <span>{translate('每日讀經')}</span>
                </button>
                <button
                  type="button"
                  className={activePlanSection === 'resources' ? 'is-active' : ''}
                  onClick={() => setActivePlanSection('resources')}
                >
                  <EuiIcon type="link" />
                  <span>{translate('相關內容')}</span>
                  <small>{selectedDay?.resources?.length || 0}</small>
                </button>
                <button
                  type="button"
                  className={activePlanSection === 'participants' ? 'is-active' : ''}
                  onClick={() => setActivePlanSection('participants')}
                >
                  <EuiIcon type="userAvatar" />
                  <span>{translate('一起讀經的人')}</span>
                  <small>{selectedPlan.participants?.length || 0}</small>
                </button>
              </nav>
            )}
          </aside>
          )}

          <section className="reading-plan-detail">
            {selectedPlan && (
              <div className="reading-plan-focus-bar">
                <div className="reading-plan-focus-summary">
                  <strong>{translate(selectedPlan.name)}</strong>
                </div>
                <div className="reading-plan-focus-actions">
                  <EuiButton
                    size="s"
                    iconType="list"
                    onClick={() => setShowPlanLibrary(value => !value)}
                  >
                    {translate('切換計劃')}
                  </EuiButton>
                  <EuiPopover
                    button={(
                      <EuiButtonEmpty
                        size="s"
                        iconType="boxesVertical"
                        onClick={() => setShowFocusMenu(value => !value)}
                      >
                        {translate('更多')}
                      </EuiButtonEmpty>
                    )}
                    isOpen={showFocusMenu}
                    closePopover={() => setShowFocusMenu(false)}
                    anchorPosition="downRight"
                    panelPaddingSize="s"
                  >
                    <div className="reading-plan-focus-menu">
                      {selectedPlan.isJoined && selectedPlanTiming.startDate && (
                        <EuiButtonEmpty
                          size="s"
                          iconType="calendar"
                          onClick={() => {
                            setShowCheckInCalendar(value => !value);
                            setShowFocusMenu(false);
                          }}
                        >
                          {translate(showCheckInCalendar ? '收起打卡日曆' : '我的打卡日曆')}
                        </EuiButtonEmpty>
                      )}
                      <EuiButtonEmpty
                        size="s"
                        iconType="calendar"
                        onClick={() => {
                          setShowDayNavigation(value => !value);
                          setShowFocusMenu(false);
                        }}
                      >
                        {translate(showDayNavigation ? '收起日期選擇' : '選擇其它日期')}
                      </EuiButtonEmpty>
                      <EuiButtonEmpty
                        size="s"
                        iconType="iInCircle"
                        onClick={() => {
                          setShowPlanDetails(value => !value);
                          setShowFocusMenu(false);
                        }}
                      >
                        {translate(showPlanDetails ? '收起計劃詳情' : '計劃詳情與設定')}
                      </EuiButtonEmpty>
                    </div>
                  </EuiPopover>
                </div>
              </div>
            )}
            {!selectedPlan ? (
              <EuiPanel className="reading-plan-empty-focus">
                <EuiText><p>{translate(plans.length ? '請選擇一個讀經計劃。' : '目前還沒有讀經計劃。')}</p></EuiText>
                <div>
                  {plans.length > 0 && (
                    <EuiButton iconType="list" onClick={() => setShowPlanLibrary(true)}>
                      {translate('選擇計劃')}
                    </EuiButton>
                  )}
                  {!shareCode && currentUser && (
                    <EuiButton fill={!plans.length} onClick={openCreatePlan}>
                      {translate('建立讀經計劃')}
                    </EuiButton>
                  )}
                </div>
              </EuiPanel>
            ) : (
              <>
                {showCheckInCalendar && selectedPlan.isJoined && selectedPlanTiming.startDate && (
                  <ReadingPlanCheckInCalendar
                    key={selectedPlan.id}
                    startDate={selectedPlanTiming.startDate}
                    days={selectedPlan.days || []}
                    selectedDayIndex={selectedDayIndex}
                    completedDays={selectedPlan.completedDays}
                    language={language}
                    translate={translate}
                    onClose={() => setShowCheckInCalendar(false)}
                    onSelectDay={index => {
                      setSelectedDayIndex(index);
                      setActivePlanSection('daily');
                      setShowPlanDetails(false);
                      setShowCheckInCalendar(false);
                      window.requestAnimationFrame(() => {
                        document.getElementById('reading-plan-current-day')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      });
                    }}
                  />
                )}
                {showPlanDetails && (
                <EuiPanel
                  className={`reading-plan-overview${safeCoverImageUrl(displayedCoverForPlan(selectedPlan)) ? ' has-cover' : ''}`}
                  style={coverBackgroundStyle(displayedCoverForPlan(selectedPlan))}
                >
                  <div className="reading-plan-title-row">
                    <div>
                      <EuiTitle size="l"><h2>{translate(selectedPlan.name)}</h2></EuiTitle>
                      <EuiText color="subdued"><p>{translate(selectedPlan.description || '一起按進度讀經、分享領受。')}</p></EuiText>
                      <div className="reading-plan-timing-summary">
                        <EuiBadge color="hollow">{translate(selectedPlan.startMode === 'synchronized' ? '同步日期進度' : '各自起算')}</EuiBadge>
                        <span>{translate('建立者：')}{translate(selectedPlan.ownerName || '未知')}</span>
                        {selectedPlanTiming.startDate && (
                          <span>{translate('開始：')}{selectedPlanTiming.startDate}</span>
                        )}
                        {selectedPlanTiming.endDate && (
                          <span>{translate(isPlanCompleted(selectedPlan) ? '結束：' : '預計結束：')}{selectedPlanTiming.endDate}</span>
                        )}
                      </div>
                    </div>
                    <EuiBadge color="primary">{selectedPlan.durationDays} {translate('天')}</EuiBadge>
                  </div>
                  {selectedPlan.isArchived && (
                    <EuiCallOut color="warning" title={translate('這個計劃已封存')}>
                      <p>{translate('內容與參與進度仍會保留，但目前不能加入、打卡或分享。')}</p>
                    </EuiCallOut>
                  )}
                  {!selectedPlan.isArchived && isPlanCompleted(selectedPlan) && (
                    <EuiCallOut color="success" title={translate('這個計劃已完成')}>
                      <p>{translate('你已完成全部讀經日程，仍可回來重溫經文、相關內容與讀經筆記。')}</p>
                    </EuiCallOut>
                  )}
                  {!selectedPlan.isArchived && selectedPlanTiming.isUpcoming && (
                    <EuiCallOut color="primary" title={translate('這個計劃尚未開始')}>
                      <p>{translate('計劃會在指定的開始日期進入第 1 天。')}</p>
                    </EuiCallOut>
                  )}
                  {!selectedPlan.isArchived && selectedPlanTiming.isOverdue && (
                    <EuiCallOut color="warning" title={translate('計劃日期已結束，但尚未完成全部打卡')}>
                      <p>{selectedPlan.startMode === 'synchronized'
                        ? translate('這是同步日期計劃；可以繼續補讀和重溫。若要開始新一輪，請由建立者建立新的同步計劃。')
                        : translate('你可以繼續補讀，或選擇新的日期重新開始；舊有打卡和筆記不會被刪除。')}</p>
                      {selectedPlan.startMode !== 'synchronized' && selectedPlan.isJoined && (
                        <EuiButton size="s" iconType="refresh" onClick={openRestartModal} disabled={busy}>
                          {translate('重新開始計劃')}
                        </EuiButton>
                      )}
                    </EuiCallOut>
                  )}
                  {!selectedPlan.isOwner && (selectedPlan.myReminders || []).length > 0 && (
                    <EuiCallOut color="primary" title={translate('計劃建立者提醒你完成讀經')}>
                      <p>{translate('尚未打卡：')} {(selectedPlan.myReminders || [])
                        .map(reminder => `${translate('第')}${reminder.dayNumber}${translate('天')} ${translate(reminder.title)}`)
                        .join('、')}</p>
                    </EuiCallOut>
                  )}
                  <EuiFlexGroup className="reading-plan-overview-actions" gutterSize="s" wrap responsive={false}>
                    {shareCode && (
                      <EuiFlexItem grow={false}>
                        <EuiButton size="s" iconType="list" onClick={openMyReadingPlans} disabled={busy}>
                          {translate('我的讀經計劃')}
                        </EuiButton>
                      </EuiFlexItem>
                    )}
                    <EuiFlexItem grow={false}>
                      {selectedPlan.isJoined && !selectedPlan.isOwner ? (
                        <EuiButton
                          size="s"
                          color="danger"
                          iconType="exit"
                          onClick={openLeaveModal}
                          disabled={busy}
                        >
                          {translate('退出計劃')}
                        </EuiButton>
                      ) : (
                        <EuiButton
                          size="s"
                          fill={!selectedPlan.isJoined}
                          iconType={selectedPlan.isJoined ? 'check' : 'userAvatar'}
                          onClick={joinPlan}
                          disabled={selectedPlan.isJoined || selectedPlan.isArchived || busy}
                        >
                          {translate(selectedPlan.isJoined ? '已參與' : '加入計劃')}
                        </EuiButton>
                      )}
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                      <EuiButton
                        size="s"
                        iconType="share"
                        onClick={sharePlan}
                        disabled={busy || selectedPlan.isArchived || selectedPlan.visibility === 'private' || (!selectedPlan.isJoined && !selectedPlan.isOwner)}
                      >
                        {translate('分享')}
                      </EuiButton>
                    </EuiFlexItem>
                    {selectedPlan.isOwner && !selectedPlan.isArchived && (
                      <EuiFlexItem grow={false}>
                        <EuiButton size="s" iconType="pencil" onClick={openEditPlan} disabled={busy}>
                          {translate('修改計劃')}
                        </EuiButton>
                      </EuiFlexItem>
                    )}
                    {selectedPlan.isOwner && (
                      <EuiFlexItem grow={false}>
                        {selectedPlan.isArchived ? (
                          <EuiButton size="s" color="success" iconType="refresh" onClick={restorePlan} disabled={busy}>
                            {translate('恢復計劃')}
                          </EuiButton>
                        ) : (
                          <EuiButton size="s" color="danger" iconType="trash" onClick={openDeleteModal} disabled={busy}>
                            {translate('刪除計劃')}
                          </EuiButton>
                        )}
                      </EuiFlexItem>
                    )}
                  </EuiFlexGroup>
                  {selectedPlan.isJoined && (
                    <div className="reading-plan-progress">
                      <div><strong>{translate('我的進度')}</strong><span>{selectedPlan.completedDays}/{selectedPlan.durationDays}</span></div>
                      <EuiProgress value={progressValue} max={100} color="success" size="m" />
                    </div>
                  )}
                </EuiPanel>
                )}

                {showDayNavigation && (
                <div className="reading-plan-day-navigation">
                  <EuiButtonEmpty
                    iconType="arrowLeft"
                    disabled={selectedDayIndex === 0}
                    onClick={() => setSelectedDayIndex(index => Math.max(index - 1, 0))}
                  >{translate('上一天')}</EuiButtonEmpty>
                  <EuiSelect
                    compressed
                    value={selectedDayIndex}
                    options={(selectedPlan.days || []).map((day, index) => ({
                      value: index,
                      text: `${translate('第')} ${day.dayNumber} ${translate('天')} · ${translate(dayTitleWithoutNumber(day) || day.scriptureReferences)}`,
                    }))}
                    onChange={event => setSelectedDayIndex(Number(event.target.value))}
                  />
                  <EuiButtonEmpty
                    iconSide="right"
                    iconType="arrowRight"
                    disabled={selectedDayIndex >= selectedPlan.days.length - 1}
                    onClick={() => setSelectedDayIndex(index => Math.min(index + 1, selectedPlan.days.length - 1))}
                  >{translate('下一天')}</EuiButtonEmpty>
                </div>
                )}

                {activePlanSection === 'daily' && selectedDay && (
                  <EuiPanel id="reading-plan-current-day" className="reading-plan-day-panel">
                    <div className="reading-plan-day-heading">
                      <div className="reading-plan-day-title">
                        <small>{translate('第')} {selectedDay.dayNumber} {translate('天')}</small>
                        {/* <EuiTitle size="m"><h1>{translate(dayTitleWithoutNumber(selectedDay) || selectedDay.scriptureReferences)}</h1></EuiTitle> */}
                        <EuiButtonIcon
                          iconType="reporter"
                          display="empty"
                          aria-label={translate('打開經文')}
                          onClick={() => openScripture()}
                        />
                      </div>
                      {selectedDayDate && (
                        <div className="reading-plan-day-date-row">
                          <time dateTime={selectedDayDate}>
                            {selectedDayDate}
                            {selectedDayDate === todayValue() && <span>{translate('今天')}</span>}
                          </time>
                          {todayPlanDayIndex >= 0 && selectedDayIndex !== todayPlanDayIndex && (
                            <button
                              type="button"
                              className="reading-plan-return-today"
                              onClick={() => setSelectedDayIndex(todayPlanDayIndex)}
                            >
                              {translate('回到本日')}
                            </button>
                          )}
                        </div>
                      )}
                      <EuiButton
                        className="reading-plan-checkin-floating"
                        fill={!selectedDay.completed}
                        color={selectedDay.completed ? 'success' : 'primary'}
                        iconType={selectedDay.completed ? 'checkInCircleFilled' : 'check'}
                        onClick={toggleCheckIn}
                        disabled={busy || selectedPlan.isArchived}
                      >
                        {translate(selectedDay.completed ? '已打卡' : '完成打卡')}
                      </EuiButton>
                    </div>
                    {selectedDay.audioUrl && (
                      <div className="reading-plan-audio">
                        <strong>{translate('聖經朗讀')}</strong>
                        <ResponsiveAudioPlayer
                          preload="metadata"
                          src={selectedDay.audioUrl}
                          aria-label={translate('聖經朗讀')}
                        />
                      </div>
                    )}
                    {selectedDay.summary && (
                      <EuiText className="reading-plan-day-summary">
                        <p title={translate(selectedDay.summary)}>{translate(selectedDay.summary)}</p>
                      </EuiText>
                    )}
                    <ReadingPlanDayScripture
                      scriptureReferences={selectedDay.scriptureReferences}
                      language={language}
                    />
                    <EuiText size="xs" color="subdued"><p>{selectedDay.checkInCount} {translate('人已完成本日打卡')}</p></EuiText>
                  </EuiPanel>
                )}

                {activePlanSection === 'resources' && (
                <EuiPanel className="reading-plan-resources">
                  <div className="reading-plan-section-title">
                    <EuiTitle size="s"><h3>{translate('相關內容')}</h3></EuiTitle>
                    {!selectedPlan.isArchived && selectedDay && (selectedPlan.isJoined || selectedPlan.isOwner) && (
                      <div className="reading-plan-section-actions">
                        <EuiButton size="s" iconType="search" onClick={openResourceSearch}>{translate('搜尋加入')}</EuiButton>
                        <EuiButtonEmpty size="s" onClick={openManualResource}>{translate('手動加入')}</EuiButtonEmpty>
                        <EuiButtonEmpty size="s" iconType="editorComment" onClick={openReadingNote}>{translate('筆記')}</EuiButtonEmpty>
                      </div>
                    )}
                  </div>
                  {!selectedDay?.resources?.length ? (
                    <EuiText size="s" color="subdued"><p>{translate('本日暫無相關內容。')}</p></EuiText>
                  ) : (
                    <div className="reading-plan-resource-grid">
                      {selectedDay.resources.map(resource => {
                        const isNote = resource.resourceType === 'note';
                        const isScripture = resource.resourceType === 'scripture';
                        const isPlayableVideo = ['song', 'sermon', 'video'].includes(resource.resourceType);
                        const isExternalLink = /^https?:\/\//i.test(resource.link || '');
                        const sourceName = String(resource.sourceName || '').trim();
                        const scriptureReference = isScripture
                          ? (!['經文', '经文'].includes(sourceName) ? sourceName : (/\d+[:：]\d+/.test(resource.resourceId || '') ? resource.resourceId : ''))
                          : '';
                        const isExpanded = expandedResourceIds.includes(resource.id);
                        const canExpand = !isNote && (
                          String(resource.title || '').trim().length > 18
                          || String(resource.description || '').trim().length > 45
                        );
                        return (
                          <article className={`reading-plan-resource-card${isNote ? ' is-note' : ''}${isExpanded ? ' is-expanded' : ''}`} key={resource.id}>
                            <div className="reading-plan-resource-heading">
                              <EuiBadge color={isNote ? 'warning' : 'hollow'}>
                                {translate(resourceTypeLabels[resource.resourceType] || '其他內容')}
                              </EuiBadge>
                              <EuiBadge color={resource.isSharedWithParticipants ? 'success' : 'hollow'}>
                                {translate(resource.isSharedWithParticipants ? '一起讀經可見' : '僅自己可見')}
                              </EuiBadge>
                              <strong>
                                {scriptureReference ? `${translate(scriptureReference)}　` : ''}
                                {translate(resource.title)}
                              </strong>
                            </div>
                            {!isScripture && resource.sourceName && (
                              <small>{translate(resource.sourceName)}</small>
                            )}
                            {(isNote || resource.isSharedWithParticipants) && resource.createdByName && (
                              <small className="reading-plan-resource-sharer">
                                {translate(isNote ? '筆記者' : '分享者')}：{translate(resource.createdByName)}
                              </small>
                            )}
                            {resource.description && (isNote ? (
                              <details className="reading-plan-note-details">
                                <summary>{translate('查看筆記')}</summary>
                                <p>{translate(resource.description)}</p>
                              </details>
                            ) : <p className="reading-plan-resource-description">{translate(resource.description)}</p>)}
                            {canExpand && (
                              <EuiButtonEmpty
                                className="reading-plan-resource-expand"
                                size="xs"
                                flush="left"
                                iconType={isExpanded ? 'arrowUp' : 'arrowDown'}
                                onClick={() => setExpandedResourceIds(current => (
                                  current.includes(resource.id)
                                    ? current.filter(id => id !== resource.id)
                                    : [...current, resource.id]
                                ))}
                              >
                                {translate(isExpanded ? '收起' : '展開全部')}
                              </EuiButtonEmpty>
                            )}
                            {isExternalLink && (isPlayableVideo || !isScripture) && (
                              <div className="reading-plan-resource-links">
                                {isPlayableVideo && (
                                  <EuiButtonEmpty className="reading-plan-resource-play" size="xs" iconType="play" onClick={() => { setMessage(null); setPlayingResource(resource); }}>
                                    {translate('站內播放')}
                                  </EuiButtonEmpty>
                                )}
                                {!isScripture && (
                                  <a className="reading-plan-resource-link" href={resource.link} target="_blank" rel="noreferrer">
                                    {translate('查看來源')} <EuiIcon type="popout" size="s" />
                                  </a>
                                )}
                              </div>
                            )}
                            {(resource.canEdit || resource.canDelete) && (
                              <div className="reading-plan-resource-actions">
                                <EuiPopover
                                  button={(
                                    <EuiButtonIcon
                                      iconType="boxesVertical"
                                      display="empty"
                                      aria-label={translate('更多操作')}
                                      onClick={() => setResourceActionMenuId(current => current === resource.id ? null : resource.id)}
                                    />
                                  )}
                                  isOpen={resourceActionMenuId === resource.id}
                                  closePopover={() => setResourceActionMenuId(null)}
                                  anchorPosition="downRight"
                                  panelPaddingSize="s"
                                >
                                  <div className="reading-plan-resource-action-menu">
                                    {resource.canEdit && (
                                      <EuiButtonEmpty
                                        size="s"
                                        iconType="pencil"
                                        onClick={() => {
                                          setResourceActionMenuId(null);
                                          openEditResource(resource);
                                        }}
                                      >
                                        {translate('修改')}
                                      </EuiButtonEmpty>
                                    )}
                                    {resource.canDelete && (
                                      <EuiButtonEmpty
                                        size="s"
                                        color="danger"
                                        iconType="trash"
                                        onClick={() => {
                                          setResourceActionMenuId(null);
                                          deleteResource(resource);
                                        }}
                                        disabled={busy}
                                      >
                                        {translate('刪除')}
                                      </EuiButtonEmpty>
                                    )}
                                  </div>
                                </EuiPopover>
                              </div>
                            )}
                          </article>
                        );
                      })}
                    </div>
                  )}
                </EuiPanel>
                )}

                {activePlanSection === 'participants' && (
                <EuiPanel className="reading-plan-participants">
                  <div className="reading-plan-participation-heading">
                    <div>
                      <EuiTitle size="s"><h3>{translate(selectedPlan.isOwner ? '計劃參與情況' : '一起讀經的人')}</h3></EuiTitle>
                      {selectedPlan.isOwner && selectedDay && (
                        <small>
                          {translate('本日完成')} {(selectedPlan.participants || []).filter(participant => (participant.completedDayIds || []).includes(selectedDay.id)).length}/{selectedPlan.participants?.length || 0}
                        </small>
                      )}
                    </div>
                    {selectedPlan.isOwner && (
                      <EuiSelect
                        compressed
                        value={selectedDayIndex}
                        aria-label={translate('查看每日打卡情況')}
                        options={(selectedPlan.days || []).map((day, index) => ({
                          value: index,
                          text: `${translate('第')} ${day.dayNumber} ${translate('天')} · ${translate(dayTitleWithoutNumber(day) || day.scriptureReferences)}`,
                        }))}
                        onChange={event => setSelectedDayIndex(Number(event.target.value))}
                      />
                    )}
                  </div>
                  <EuiSpacer size="s" />
                  <div className="reading-plan-avatar-list">
                    {(selectedPlan.participants || []).map(participant => {
                      const checkedIn = (participant.completedDayIds || []).includes(selectedDay?.id);
                      const dayIsDue = selectedDay && participant.currentDayNumber >= selectedDay.dayNumber;
                      const isCreator = participant.userId === selectedPlan.ownerUserId;
                      const reminderKey = `${selectedDay?.id}-${participant.userId}`;
                      const remindedRecently = (selectedPlan.recentReminders || []).some(reminder => (
                        reminder.readingPlanDayId === selectedDay?.id && reminder.recipientUserId === participant.userId
                      ));
                      const statusText = checkedIn ? '已打卡' : (dayIsDue ? '未打卡' : '尚未到此進度');
                      return (
                        <div className="reading-plan-participant" key={participant.userId} title={participant.name}>
                          <EuiAvatar name={participant.name || translate('參與者')} size="s" />
                          <div className="reading-plan-participant-info">
                            <strong>
                              {translate(participant.name || '參與者')}
                              {isCreator && <EuiBadge color="hollow">{translate('建立者')}</EuiBadge>}
                            </strong>
                            <small>
                              {translate('開始')} {displayDate(participant.startDate)} · {participant.currentDayNumber > 0 ? `${translate('第')}${participant.currentDayNumber}${translate('天')}` : translate('尚未開始')} · {participant.completedDays}/{selectedPlan.durationDays}
                            </small>
                          </div>
                          <div className="reading-plan-participant-actions">
                            <EuiBadge className="reading-plan-participant-status" color={checkedIn ? 'success' : (dayIsDue ? 'warning' : 'hollow')}>
                              {translate(statusText)}
                            </EuiBadge>
                            {selectedPlan.isOwner && !isCreator && dayIsDue && !checkedIn && !selectedPlan.isArchived && (
                              <EuiButtonEmpty
                                size="xs"
                                iconType="bell"
                                isLoading={reminderSendingKey === reminderKey}
                                disabled={remindedRecently || Boolean(reminderSendingKey)}
                                onClick={() => sendCheckInReminder(participant)}
                              >
                                {translate(remindedRecently ? '已提醒' : '提醒')}
                              </EuiButtonEmpty>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </EuiPanel>
                )}
              </>
            )}
          </section>
        </main>
      )}

      {showCreateModal && (
        <EuiOverlayMask className="reading-plan-overlay">
          <EuiModal onClose={closePlanEditor} className="reading-plan-editor-modal">
            <EuiModalHeader>
              <EuiModalHeaderTitle>{translate(editingTemplate ? '修改讀經模板' : (editingPlan ? '修改讀經計劃' : '建立讀經計劃'))}</EuiModalHeaderTitle>
            </EuiModalHeader>
            {renderModalMessage()}
            <EuiModalBody>
              {!editingPlan && !editingTemplate && (
                <>
                  <EuiFormRow label={translate('一鍵套用缺省計劃')} fullWidth>
                    <div className="reading-plan-template-grid">
                      {planTemplates.map(template => (
                        <div className="reading-plan-template-item" key={template.id}>
                          <button
                            type="button"
                            disabled={busy}
                            className={`reading-plan-template-card${planForm.templateId === template.id ? ' is-selected' : ''}`}
                            onClick={() => applyTemplate(template)}
                          >
                            <strong>{translate(template.name)}</strong>
                            <span>{translate(template.description)}</span>
                            <small>{template.durationDays} {translate('天')}</small>
                          </button>
                          {isAdmin && (
                            <EuiButtonIcon
                              className="reading-plan-template-edit"
                              iconType="pencil"
                              aria-label={translate('修改模板')}
                              title={translate('修改模板')}
                              isDisabled={busy}
                              onClick={() => openEditTemplate(template)}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </EuiFormRow>
                  <EuiCallOut color="primary" title={translate('選擇模板後會自動生成每日安排')}>
                    <p>{translate('生成後可以按天修改經文範圍、標題和簡介。')}</p>
                  </EuiCallOut>
                  <EuiSpacer size="m" />
                </>
              )}
              <EuiFormRow label={translate('計劃名稱')}>
                <EuiFieldText value={planForm.name} onChange={event => setPlanForm({ ...planForm, name: event.target.value })} />
              </EuiFormRow>
              <EuiFormRow label={translate('簡介')}>
                <EuiTextArea value={planForm.description} onChange={event => setPlanForm({ ...planForm, description: event.target.value })} />
              </EuiFormRow>
              {!editingTemplate && (
                <EuiFormRow
                  label={translate('計劃封面')}
                  helpText={translate(isAndroidApp
                    ? '目前 App 內可以選擇內建圖片。'
                    : '可以選擇內建圖片或上傳自己的圖片（最大 5 MB）。')}
                >
                  <div className="reading-plan-cover-editor">
                    <div className="reading-plan-cover-options">
                      <button
                        type="button"
                        className={`reading-plan-cover-option is-empty${!planForm.coverImageUrl ? ' is-selected' : ''}`}
                        onClick={() => setPlanForm({ ...planForm, coverImageUrl: '' })}
                      >
                        {translate('預設')}
                      </button>
                      {builtInPlanCovers.map(cover => (
                        <button
                          type="button"
                          key={cover.url}
                          className={`reading-plan-cover-option${planForm.coverImageUrl === cover.url ? ' is-selected' : ''}`}
                          style={{ backgroundImage: `url("${cover.url}")` }}
                          title={translate(cover.label)}
                          aria-label={translate(cover.label)}
                          onClick={() => setPlanForm({ ...planForm, coverImageUrl: cover.url })}
                        />
                      ))}
                    </div>
                    {!isAndroidApp && (
                      <>
                        <EuiButton
                          type="button"
                          className={`reading-plan-cover-upload${coverUploading ? ' is-loading' : ''}`}
                          iconType="image"
                          fullWidth
                          isLoading={coverUploading}
                          disabled={coverUploading}
                          onClick={openPlanCoverPicker}
                        >
                          {translate(coverUploading ? '正在上傳……' : '上傳圖片')}
                        </EuiButton>
                        <input
                          ref={coverFileInputRef}
                          className="reading-plan-cover-file-input"
                          type="file"
                          accept="image/*,.jpg,.jpeg,.png,.webp,.gif,.heic,.heif"
                          disabled={coverUploading}
                          onChange={selectPlanCover}
                          tabIndex="-1"
                          aria-hidden="true"
                        />
                      </>
                    )}
                    {safeCoverImageUrl(planForm.coverImageUrl) && (
                      <div
                        className="reading-plan-cover-preview"
                        style={coverBackgroundStyle(planForm.coverImageUrl)}
                        role="img"
                        aria-label={translate('封面圖片預覽')}
                      >
                        <strong>{translate(planForm.name || '讀經計劃')}</strong>
                      </div>
                    )}
                  </div>
                </EuiFormRow>
              )}
              <EuiFlexGroup gutterSize="s">
                <EuiFlexItem>
                  <EuiFormRow label={translate('分類')}>
                    <EuiFieldText value={planForm.category} onChange={event => setPlanForm({ ...planForm, category: event.target.value })} />
                  </EuiFormRow>
                </EuiFlexItem>
                <EuiFlexItem>
                  <EuiFormRow label={translate('可見範圍')}>
                    <EuiSelect value={planForm.visibility} onChange={event => setPlanForm({ ...planForm, visibility: event.target.value })} options={[
                      { value: 'shareable', text: translate('分享後可加入') },
                      { value: 'private', text: translate('私人') },
                    ]} />
                  </EuiFormRow>
                </EuiFlexItem>
              </EuiFlexGroup>

              {!editingTemplate && (
                <>
                  <EuiFlexGroup gutterSize="s">
                    <EuiFlexItem>
                      <EuiFormRow label={translate('加入與起算方式')} helpText={planForm.startMode === 'synchronized'
                        ? translate('所有參與者都按同一日期計算第 1 天，適合全年同步讀經。')
                        : translate('每位參與者加入時選擇自己的開始日期。')}>
                        <EuiSelect
                          value={planForm.startMode}
                          disabled={Boolean(editingPlan?.hasOtherParticipants)}
                          onChange={event => setPlanForm({ ...planForm, startMode: event.target.value })}
                          options={[
                            { value: 'individual', text: translate('各自起算') },
                            { value: 'synchronized', text: translate('同步日期進度') },
                          ]}
                        />
                      </EuiFormRow>
                    </EuiFlexItem>
                    {planForm.startMode === 'synchronized' && (
                      <EuiFlexItem>
                        <EuiFormRow label={translate('統一起始日')}>
                          <EuiFieldText
                            type="date"
                            value={planForm.fixedStartDate}
                            disabled={Boolean(editingPlan?.hasOtherParticipants)}
                            onChange={event => setPlanForm({ ...planForm, fixedStartDate: event.target.value })}
                          />
                        </EuiFormRow>
                      </EuiFlexItem>
                    )}
                  </EuiFlexGroup>
                  <EuiSpacer size="m" />
                </>
              )}

              {editingPlan?.hasOtherParticipants && (
                <>
                  <EuiCallOut color="warning" title={translate('已有其他人參與，讀經安排受到保護')}>
                    <p>{translate('不能增加計劃天數或修改每日經文範圍；計劃名稱、簡介、每日標題和每日簡介仍可修改。')}</p>
                  </EuiCallOut>
                  <EuiSpacer size="m" />
                </>
              )}
              <div className="reading-plan-day-editor-title">
                <EuiTitle size="xs"><h3>{translate('每日安排')}（{planForm.days.length} {translate('天')}）</h3></EuiTitle>
                <EuiButtonEmpty
                  size="s"
                  onClick={addPlanDay}
                  disabled={Boolean(editingPlan?.hasOtherParticipants)}
                >
                  <span aria-hidden="true">＋</span>&nbsp;{translate('新增一天')}
                </EuiButtonEmpty>
              </div>
              <div className="reading-plan-day-editor-list">
                {planForm.days.map((day, index) => {
                  const validation = scriptureValidation[index];
                  const scriptureLocked = Boolean(editingPlan?.hasOtherParticipants && day.id);
                  return (
                    <section className={`reading-plan-day-editor${validation?.isValid === false ? ' is-invalid' : ''}`} key={day.id || `new-${index}`}>
                      <div className="reading-plan-day-editor-header">
                        <strong>{translate('第')} {index + 1} {translate('天')}</strong>
                        <EuiButtonEmpty
                          size="xs"
                          color="danger"
                          iconType="trash"
                          onClick={() => removePlanDay(index)}
                          disabled={planForm.days.length <= 1}
                        >
                          {translate('刪除')}
                        </EuiButtonEmpty>
                      </div>
                      <div className="reading-plan-day-editor-fields">
                        <div>
                          <EuiFormRow
                            label={translate('經文範圍')}
                            isInvalid={validation?.isValid === false}
                            error={validation?.isValid === false ? translate(validation.message || '經文範圍不正確。') : undefined}
                            helpText={scriptureLocked ? translate('已有其他人參與，經文範圍不能修改。') : undefined}
                          >
                            <EuiFieldText
                              value={day.scriptureReferences}
                              placeholder={translate('例如：創1-2、約3:16-18')}
                              isInvalid={validation?.isValid === false}
                              isLoading={validation?.status === 'checking'}
                              readOnly={scriptureLocked}
                              onChange={event => updatePlanDay(index, 'scriptureReferences', event.target.value)}
                              onBlur={() => { if (!scriptureLocked) validateDayScripture(index); }}
                            />
                          </EuiFormRow>
                          {validation?.isValid && <small className="reading-plan-scripture-valid">✓ {translate('經文範圍正確')}</small>}
                        </div>
                        <EuiFormRow label={translate('標題')}>
                          <EuiFieldText value={day.title} onChange={event => updatePlanDay(index, 'title', event.target.value)} />
                        </EuiFormRow>
                        <EuiFormRow className="reading-plan-day-summary-field" label={translate('簡介')}>
                          <EuiTextArea rows={2} value={day.summary} onChange={event => updatePlanDay(index, 'summary', event.target.value)} />
                        </EuiFormRow>
                      </div>
                    </section>
                  );
                })}
              </div>
            </EuiModalBody>
            <EuiModalFooter>
              <EuiButtonEmpty onClick={closePlanEditor}>{translate('取消')}</EuiButtonEmpty>
              <EuiButton fill onClick={savePlan} isLoading={busy}>
                {translate(editingTemplate || editingPlan ? '儲存修改' : '建立')}
              </EuiButton>
            </EuiModalFooter>
          </EuiModal>
        </EuiOverlayMask>
      )}
      {coverCrop && (
        <EuiOverlayMask className="reading-plan-overlay reading-plan-cover-crop-overlay">
          <EuiModal className="reading-plan-cover-crop-modal" onClose={closeCoverCrop}>
            <EuiModalHeader>
              <EuiModalHeaderTitle>{translate('裁剪計劃封面')}</EuiModalHeaderTitle>
            </EuiModalHeader>
            <EuiModalBody>
              {coverCropError && (
                <>
                  <EuiCallOut color="danger" title={translate('封面圖片上傳失敗')}>
                    <p>{coverCropError}</p>
                  </EuiCallOut>
                  <EuiSpacer size="s" />
                </>
              )}
              <EuiText size="s" color="subdued">
                <p>{translate('框內內容會按頁面封面的 16:5 比例顯示。可拖動圖片或使用滑桿微調。')}</p>
              </EuiText>
              <EuiSpacer size="s" />
              <ReadingPlanCoverCropper crop={coverCrop} onChange={setCoverCrop} language={language} />
            </EuiModalBody>
            <EuiModalFooter>
              <EuiButtonEmpty onClick={closeCoverCrop} disabled={coverUploading}>{translate('取消')}</EuiButtonEmpty>
              <EuiButton fill onClick={uploadPlanCover} isLoading={coverUploading}>{translate('確認裁剪並上傳')}</EuiButton>
            </EuiModalFooter>
          </EuiModal>
        </EuiOverlayMask>
      )}
      {playingResource && (
        <EuiOverlayMask className="reading-plan-overlay">
          <EuiModal className="reading-plan-song-modal" onClose={() => setPlayingResource(null)}>
            <EuiModalHeader><EuiModalHeaderTitle>{translate(playingResource.title || '播放詩歌')}</EuiModalHeaderTitle></EuiModalHeader>
            {renderModalMessage()}
            <EuiModalBody><YTModal videoURL={playingResource.link} /></EuiModalBody>
            <EuiModalFooter><EuiButton onClick={() => setPlayingResource(null)}>{translate('關閉')}</EuiButton></EuiModalFooter>
          </EuiModal>
        </EuiOverlayMask>
      )}

      {showResourceModal && (
        <EuiOverlayMask className="reading-plan-overlay">
          <EuiModal className="reading-plan-share-modal" onClose={closeResourceEditor}>
            <EuiModalHeader>
              <EuiModalHeaderTitle>
                {translate(editingResource
                  ? (resourceModalMode === 'note' ? '修改讀經筆記' : '修改相關內容')
                  : (resourceModalMode === 'note' ? '加入讀經筆記' : '加入相關內容'))}
              </EuiModalHeaderTitle>
            </EuiModalHeader>
            {renderModalMessage()}
            <EuiModalBody>
              {resourceModalMode !== 'note' && <EuiFormRow label={translate('內容類型')}><EuiSelect value={resourceForm.resourceType} onChange={e => setResourceForm({ ...resourceForm, resourceType: e.target.value })} options={Object.entries(resourceTypeLabels).filter(([value]) => value !== 'note').map(([value, text]) => ({ value, text: translate(text) }))} /></EuiFormRow>}
              <EuiFormRow label={translate('標題')}><EuiFieldText value={resourceForm.title} onChange={e => setResourceForm({ ...resourceForm, title: e.target.value })} /></EuiFormRow>
              {resourceModalMode !== 'note' && <EuiFormRow label={translate('來源')}><EuiFieldText value={resourceForm.sourceName} onChange={e => setResourceForm({ ...resourceForm, sourceName: e.target.value })} /></EuiFormRow>}
              {resourceModalMode !== 'note' && <EuiFormRow label={translate('鏈接')}><EuiFieldText value={resourceForm.link} onChange={e => setResourceForm({ ...resourceForm, link: e.target.value })} /></EuiFormRow>}
              <EuiFormRow label={translate(resourceModalMode === 'note' ? '筆記內容' : '簡介')}><EuiTextArea rows={resourceModalMode === 'note' ? 7 : 4} value={resourceForm.description} onChange={e => setResourceForm({ ...resourceForm, description: e.target.value })} /></EuiFormRow>
              <EuiFormRow helpText={translate('未開啟時，只有你自己可以看到這項內容；本次選擇會作為以後新增內容的預設值。')}>
                <EuiSwitch
                  label={translate('允許一起讀經的人查看')}
                  checked={resourceForm.isSharedWithParticipants}
                  onChange={event => {
                    const checked = event.target.checked;
                    setShareResourcesWithParticipantsDefault(checked);
                    setResourceForm({ ...resourceForm, isSharedWithParticipants: checked });
                  }}
                />
              </EuiFormRow>
            </EuiModalBody>
            <EuiModalFooter>
              <EuiButtonEmpty onClick={closeResourceEditor}>{translate('取消')}</EuiButtonEmpty>
              <EuiButton fill onClick={saveResource} isLoading={busy}>{translate(editingResource ? '儲存' : '加入')}</EuiButton>
            </EuiModalFooter>
          </EuiModal>
        </EuiOverlayMask>
      )}

      {showJoinModal && selectedPlan && (
        <EuiOverlayMask className="reading-plan-overlay">
          <EuiModal onClose={closeJoinModal}>
            <EuiModalHeader><EuiModalHeaderTitle>{translate('加入讀經計劃')}</EuiModalHeaderTitle></EuiModalHeader>
            {renderModalMessage()}
            <EuiModalBody>
              {selectedPlan.startMode === 'synchronized' ? (
                <EuiCallOut color="primary" title={translate('此計劃採用同步日期進度')}>
                  <p>{translate('所有參與者都從同一天起算。統一起始日：')} {String(selectedPlan.fixedStartDate || '').slice(0, 10)}</p>
                </EuiCallOut>
              ) : (
                <>
                  <EuiCallOut color="primary" title={translate('每位參與者可以有自己的開始日期')}>
                    <p>{translate('你的第1天會從所選日期開始，不會受到其他參與者進度影響。')}</p>
                  </EuiCallOut>
                  <EuiSpacer size="m" />
                  <EuiFormRow label={translate('我的開始日期')}>
                    <EuiFieldText type="date" value={joinStartDate} onChange={event => setJoinStartDate(event.target.value)} />
                  </EuiFormRow>
                </>
              )}
            </EuiModalBody>
            <EuiModalFooter>
              <EuiButtonEmpty onClick={closeJoinModal}>{translate('取消')}</EuiButtonEmpty>
              <EuiButton fill onClick={confirmJoinPlan} isLoading={busy}>{translate('確認加入')}</EuiButton>
            </EuiModalFooter>
          </EuiModal>
        </EuiOverlayMask>
      )}

      {showRestartModal && selectedPlan && (
        <EuiOverlayMask className="reading-plan-overlay">
          <EuiModal onClose={() => setShowRestartModal(false)}>
            <EuiModalHeader><EuiModalHeaderTitle>{translate('重新開始讀經計劃')}</EuiModalHeaderTitle></EuiModalHeader>
            {renderModalMessage()}
            <EuiModalBody>
              <EuiCallOut color="primary" title={translate('歷史內容會保留')}>
                <p>{translate('新的進度只計算新開始日期之後的打卡；原有筆記和相關內容不會刪除。')}</p>
              </EuiCallOut>
              <EuiSpacer size="m" />
              <EuiFormRow label={translate('新的開始日期')}>
                <EuiFieldText type="date" value={restartStartDate} onChange={event => setRestartStartDate(event.target.value)} />
              </EuiFormRow>
            </EuiModalBody>
            <EuiModalFooter>
              <EuiButtonEmpty onClick={() => setShowRestartModal(false)}>{translate('取消')}</EuiButtonEmpty>
              <EuiButton fill iconType="refresh" onClick={restartPlan} isLoading={busy}>{translate('確認重新開始')}</EuiButton>
            </EuiModalFooter>
          </EuiModal>
        </EuiOverlayMask>
      )}

      {showLeaveModal && selectedPlan && (
        <EuiOverlayMask className="reading-plan-overlay">
          <EuiModal onClose={closeLeaveModal}>
            <EuiModalHeader><EuiModalHeaderTitle>{translate('退出讀經計劃？')}</EuiModalHeaderTitle></EuiModalHeader>
            {renderModalMessage()}
            <EuiModalBody>
              <EuiCallOut color="warning" title={translate('退出後，計劃將不再顯示在你的清單中')}>
                <p>{translate('你的既有打卡記錄會保留；日後透過分享連結重新加入時，可以繼續原有進度。')}</p>
              </EuiCallOut>
            </EuiModalBody>
            <EuiModalFooter>
              <EuiButtonEmpty onClick={closeLeaveModal}>{translate('取消')}</EuiButtonEmpty>
              <EuiButton color="danger" fill onClick={leavePlan} isLoading={busy}>
                {translate('確認退出')}
              </EuiButton>
            </EuiModalFooter>
          </EuiModal>
        </EuiOverlayMask>
      )}

      {showResourceSearchModal && selectedDay && (
        <EuiOverlayMask className="reading-plan-overlay">
          <EuiModal onClose={closeResourceSearchModal} className="reading-plan-resource-search-modal">
            <EuiModalHeader><EuiModalHeaderTitle>{translate('搜尋並加入相關內容')}</EuiModalHeaderTitle></EuiModalHeader>
            {renderModalMessage()}
            <EuiModalBody>
              <div className="reading-plan-resource-search-bar">
                <EuiFieldText value={resourceSearchTerm} onChange={event => setResourceSearchTerm(event.target.value)} />
                <EuiButton iconType="search" onClick={() => searchResources(resourceSearchTerm)} isLoading={resourceSearchLoading}>{translate('搜尋')}</EuiButton>
              </div>
              <EuiSpacer size="m" />
              <div className="reading-plan-search-results">
                {resourceSearchResults.map((result, index) => {
                  const title = resultValue(result, 'title') || translate('相關內容');
                  const doctype = resultValue(result, 'doctype') || translate('內容');
                  return (
                    <EuiPanel key={`${resultValue(result, 'id')}-${index}`} paddingSize="s" className="reading-plan-search-result">
                      <div><EuiBadge>{translate(doctype)}</EuiBadge><strong>{translate(title)}</strong></div>
                      {(selectedPlan.isJoined || selectedPlan.isOwner) ? (
                        <EuiButton size="s" iconType="plus" onClick={() => addSearchResource(result)} disabled={busy}>{translate('加入')}</EuiButton>
                      ) : (
                        <EuiButton size="s" href={resultValue(result, 'link') || `/?q=${encodeURIComponent(title)}`} target="_blank">{translate('查看')}</EuiButton>
                      )}
                    </EuiPanel>
                  );
                })}
                {!resourceSearchLoading && resourceSearchResults.length === 0 && <EuiText color="subdued"><p>{translate('沒有找到可加入的相關內容。')}</p></EuiText>}
              </div>
            </EuiModalBody>
            <EuiModalFooter><EuiButtonEmpty onClick={closeResourceSearchModal}>{translate('關閉')}</EuiButtonEmpty></EuiModalFooter>
          </EuiModal>
        </EuiOverlayMask>
      )}

      {showDeleteModal && selectedPlan && (
        <EuiOverlayMask className="reading-plan-overlay">
          <EuiModal className="reading-plan-share-modal" onClose={closeDeleteModal}>
            <EuiModalHeader><EuiModalHeaderTitle>{translate('刪除讀經計劃？')}</EuiModalHeaderTitle></EuiModalHeader>
            {renderModalMessage()}
            <EuiModalBody>
              <EuiCallOut color="warning" title={translate('系統會先保護已分享的資料')}>
                <p>{translate('若尚未分享且沒有他人參與或打卡，計劃會直接刪除。若已有分享、參與者或打卡，計劃會改為封存，保留所有人的進度並停止新加入與分享。')}</p>
              </EuiCallOut>
            </EuiModalBody>
            <EuiModalFooter>
              <EuiButtonEmpty onClick={closeDeleteModal}>{translate('取消')}</EuiButtonEmpty>
              <EuiButton color="danger" fill onClick={deleteOrArchivePlan} isLoading={busy}>
                {translate('確認刪除或封存')}
              </EuiButton>
            </EuiModalFooter>
          </EuiModal>
        </EuiOverlayMask>
      )}

      {shareLink && (
        <EuiOverlayMask className="reading-plan-overlay">
          <EuiModal className="reading-plan-share-modal" onClose={() => setShareLink('')}>
            <EuiModalHeader>
              <EuiModalHeaderTitle>{translate('分享讀經計劃')}</EuiModalHeaderTitle>
            </EuiModalHeader>
            {renderModalMessage()}
            <EuiModalBody>
              <EuiFormRow label={translate('分享鏈接')} fullWidth>
                <EuiFieldText value={shareLink} readOnly fullWidth onFocus={event => event.target.select()} />
              </EuiFormRow>
              <EuiText color="subdued" size="s">
                <p>{translate('朋友可以使用此鏈接查看並加入讀經計劃。')}</p>
              </EuiText>
            </EuiModalBody>
            <EuiModalFooter>
              <EuiButtonEmpty onClick={() => setShareLink('')}>{translate('關閉')}</EuiButtonEmpty>
              {navigator.share && <EuiButton iconType="share" onClick={openNativeShare}>{translate('系統分享')}</EuiButton>}
              <EuiButton fill iconType="copyClipboard" onClick={copyPlanShareLink}>{translate('複製鏈接')}</EuiButton>
            </EuiModalFooter>
          </EuiModal>
        </EuiOverlayMask>
      )}

      {scriptureFlyout && (
        <EuiFlyout className="reading-plan-scripture-flyout" ownFocus size="m" onClose={() => setScriptureFlyout(null)} aria-labelledby="flyoutTitle">
          <EuiFlyoutBody className="reading-plan-scripture-flyout-body">
            <Verse
              key={`${scriptureFlyout.book}-${scriptureFlyout.chapter}`}
              book={scriptureFlyout.book}
              chapter={String(scriptureFlyout.chapter)}
              bookname={scriptureFlyout.bookname}
              versenumber={scriptureFlyout.verseStart
                ? (scriptureFlyout.verseEnd > scriptureFlyout.verseStart
                  ? `${scriptureFlyout.verseStart}-${scriptureFlyout.verseEnd}`
                  : String(scriptureFlyout.verseStart))
                : undefined}
              language={language}
              useFooterAudioControls
              footerAudioTargetId="reading-plan-scripture-footer-audio"
              footerNoteTargetId="reading-plan-scripture-footer-note"
              onVerseChange={selection => {
                setScriptureFlyout(current => current ? {
                  ...current,
                  book: selection.book || current.book,
                  chapter: selection.chapter || current.chapter,
                  selectedVerse: selection.verse || current.selectedVerse,
                } : current);
              }}
            />
          </EuiFlyoutBody>
          <EuiFlyoutFooter className="reading-plan-scripture-flyout-footer">
            <EuiFlexGroup alignItems="center" justifyContent="flexEnd" gutterSize="s" responsive={false} wrap>
              <EuiFlexItem className="scripture-footer-audio-slot">
                <div id="reading-plan-scripture-footer-audio" />
              </EuiFlexItem>
              <EuiFlexItem grow={false} className="scripture-footer-action">
                <div id="reading-plan-scripture-footer-note" />
              </EuiFlexItem>
              <EuiFlexItem grow={false} className="scripture-footer-action">
                <EuiButton
                  onClick={() => openResourceSearch(
                    `${scriptureFlyout.book}${scriptureFlyout.chapter}:${scriptureFlyout.selectedVerse || scriptureFlyout.verseStart || 1}`
                  )}
                >
                  {translate('搜尋')}
                </EuiButton>
              </EuiFlexItem>
              <EuiFlexItem grow={false} className="scripture-footer-action">
                <EuiButton
                  // fill
                  // iconType="cross"
                  onClick={() => setScriptureFlyout(null)}
                >
                  {translate('關閉')}
                </EuiButton>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlyoutFooter>
        </EuiFlyout>
      )}

      <LoginRequiredModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        nextUrl={typeof window !== 'undefined' ? window.location.href : '/'}
        title={translate('請先登入')}
        message={translate('登入後可以參與讀經計劃、打卡並與朋友分享。')}
      />
    </div>
  );
}

export default ReadingPlansPage;
